// =============================================================================
// landedCost.js
// The landed-cost formulas for procurement (stocked) items, in one place —
// including the INVERSE used by the Monthly Pricing page.
//
// A "cell" here is one item-period's pricing state:
//   { components: { component_code: amount }, currencies: { component_code: ccy } }
// (exactly the shape the purchase API returns). `ctx` carries the pricing
// environment: { vatRate (percent), rateOf(ccyCode) -> rate, baseCode }.
//
// Rules (mirrored from the Stocked Pricing page):
//   * Each component amount is in its OWN cell currency; totals are converted to
//     the BASE currency by that cell's rate.
//   * VAT is never entered — it is vatRate% of the CUSTOMS VALUE (every component
//     up to and including Import Duty, excluding VAT), computed on the
//     base-currency value, so the VAT amount is itself already in base currency.
//
// Both Purchase pricing pages build on this module — Stocked Pricing (forward:
// components -> landed cost) and Monthly Pricing (inverse: landed cost -> base
// price) — so the two can never disagree on what an item costs.
// =============================================================================
import { ALL_COMPONENTS, VAT_COMPONENT } from '../config/landedCostStages';

// The base purchase price — the component the inverse solves for.
export const BASE_PRICE_COMPONENT = 'base_price';

// Components forming the VAT base (the customs value): everything up to AND
// INCLUDING import duty, excluding VAT itself.
export const VAT_BASE_CODES = (() => {
  const idx = ALL_COMPONENTS.findIndex((c) => c.code === 'import_duty');
  return ALL_COMPONENTS.slice(0, idx + 1).map((c) => c.code).filter((c) => c !== VAT_COMPONENT);
})();

const round = (n, dp) => {
  const f = 10 ** dp;
  return Math.round((Number(n) || 0) * f) / f;
};

const rawOf = (cell, code) => Number(cell?.components?.[code] || 0);

// A component cell's currency. VAT is always base currency — it is computed on
// the base-currency customs value, so it must never be re-converted.
export const currencyOfComponent = (cell, code, ctx) =>
  (code === VAT_COMPONENT ? ctx.baseCode : (cell?.currencies?.[code] || ctx.baseCode));

const rateOfComponent = (cell, code, ctx) => {
  const r = Number(ctx.rateOf(currencyOfComponent(cell, code, ctx)));
  return Number.isFinite(r) && r !== 0 ? r : 1;
};

// The auto-calculated VAT amount, in BASE currency.
export const computedVat = (cell, ctx) => {
  const customsValue = VAT_BASE_CODES.reduce(
    (a, code) => a + rawOf(cell, code) * rateOfComponent(cell, code, ctx), 0);
  return round((Number(ctx.vatRate) || 0) / 100 * customsValue, 2);
};

// The value of one component, in its OWN cell currency — what the grid shows in
// that component's column (VAT is derived, everything else is as entered).
export const componentValue = (cell, code, ctx) =>
  (code === VAT_COMPONENT ? computedVat(cell, ctx) : rawOf(cell, code));

// The same component converted to BASE currency by its own cell's rate — the
// only form that is safe to add up across components.
export const componentValueBase = (cell, code, ctx) =>
  componentValue(cell, code, ctx) * rateOfComponent(cell, code, ctx);

// One stage of the build-up (e.g. "Freight"), in BASE currency — the collapsed
// stage column on the Stocked Pricing grid.
export const stageTotalBase = (cell, stage, ctx) =>
  stage.components.reduce((a, c) => a + componentValueBase(cell, c.code, ctx), 0);

// Full landed cost for the item-period, in BASE currency.
export const landedTotalBase = (cell, ctx) =>
  ALL_COMPONENTS.reduce((a, c) => a + componentValueBase(cell, c.code, ctx), 0);

// Everything in the landed total EXCEPT the base purchase price, in BASE
// currency — i.e. the fixed part when solving for the base price. VAT's share of
// the OTHER vat-base components is included here; VAT on the base price itself is
// handled by the (1 + v) factor in solveBasePrice.
export const landedExcludingBasePrice = (cell, ctx) => {
  const v = (Number(ctx.vatRate) || 0) / 100;
  const others = ALL_COMPONENTS
    .filter((c) => c.code !== VAT_COMPONENT && c.code !== BASE_PRICE_COMPONENT)
    .reduce((a, c) => a + rawOf(cell, c.code) * rateOfComponent(cell, c.code, ctx), 0);
  const vatBaseOthers = VAT_BASE_CODES
    .filter((code) => code !== BASE_PRICE_COMPONENT)
    .reduce((a, code) => a + rawOf(cell, code) * rateOfComponent(cell, code, ctx), 0);
  return others + v * vatBaseOthers;
};

// ---- INVERSE ---------------------------------------------------------------
// Given a target landed cost (BASE currency), what base purchase price gets us
// there? Derivation, with v = vatRate/100, r = the base-price cell's rate and
// K = landedExcludingBasePrice:
//
//   landed = a·r + others + v·(a·r + vatBaseOthers)
//          = a·r·(1 + v) + K
//   =>   a = (target − K) / (r · (1 + v))
//
// Returned in the base-price cell's OWN currency (that is how it is stored).
// Returns null when unreachable — the other landed-cost components already cost
// more than the target, so the base price would have to be negative.
export const solveBasePrice = (cell, targetLanded, ctx) => {
  const v = (Number(ctx.vatRate) || 0) / 100;
  const r = rateOfComponent(cell, BASE_PRICE_COMPONENT, ctx);
  const K = landedExcludingBasePrice(cell, ctx);
  const amount = (Number(targetLanded) - K) / (r * (1 + v));
  if (!Number.isFinite(amount) || amount < 0) return null;
  return round(amount, 4);
};

export default {
  landedTotalBase, stageTotalBase, solveBasePrice, computedVat,
  componentValue, componentValueBase, VAT_BASE_CODES, BASE_PRICE_COMPONENT,
};
